import * as prism from './prism.js';
/**
 * Tools for working with tagged unions.
 *
 * @example
 * type Num = tagged.Tagged<'num', number>;
 * type Str = tagged.Tagged<'str', string>;
 * type NumStr = Num | Str;
 * const numStr = tagged.build<NumStr>();
 * const one = numStr.num(1);
 * const hello = numStr.str('hello');
 * const fold = numStr.fold({
 *   num: (n) => n + 1,
 *   str: (s) => s.length,
 * });
 * const two = fold(one);
 * const five = fold(hello);
 */
const None = Symbol();
// Constructor -----------------------------------------------------------------------------------
/**
 * Create a constructor for a specific tagged union member.
 *
 * @example
 * const num = tagged.tag<Num>('num');
 * const one = num(1) // { _tag: 'num', value: 1 }
 */
export const tag = (_tag) => ((...args) => 0 in args ? { _tag, value: args[0] } : { _tag });
/**
 * Fold over the values of a union of tagged objects.
 *
 * @example
 * const foldNumStr = tagged.fold<Num | Str>();
 * const dataLast = foldNumStr({
 *   Num: (n) => n + 1,
 *   Str: (s) => s.length,
 * });
 * const three = myFold(num(2));
 */
export const fold = () => {
    const foldStrict = (...args) => {
        if (args.length === 1) {
            const [pattern] = args;
            return (data) => foldStrict(data, pattern);
        }
        const [data, pattern] = args;
        return pattern[data._tag](data.value);
    };
    return foldStrict;
};
/**
 * Create a "Refinement" for a specific tagged union member.
 *
 * @example
 * const isNum = is<NumStr, Num>('num');
 * const a: NumStr = ...;
 * if (isNum(a)) {
 *   const { value } = a;
 *   //      ^? number
 * }
 */
export const is = (t) => (a) => a._tag === t;
const foldInstance = fold();
/**
 * Build an object containing constructors and a Fold for a union of tagged types.
 *
 * @example
 * const numStr = tagged.build<NumStr>();
 * const one = numStr.num(1);
 * const helloWorld = numStr.str('hello');
 * const fold = numStr.fold({
 *   num: (n) => n + 1,
 *   str: (s) => s.length,
 * });
 * const two = fold(one);
 * const five = fold(helloWorld);
 */
export const build = () => {
    const assertions = new Proxy({}, {
        get(asserts, prop) {
            if (typeof prop !== 'string')
                return Reflect.get(asserts, prop);
            const _tag = prop;
            if (!(_tag in asserts)) {
                asserts[_tag] = is(_tag);
            }
            return asserts[_tag];
        },
    });
    const constructors = new Proxy({}, {
        get(constructors, prop) {
            const _tag = prop;
            if (!(_tag in constructors)) {
                constructors[_tag] = tag(_tag);
            }
            return constructors[_tag];
        },
    });
    return new Proxy({}, {
        get(algebra, prop) {
            if (typeof prop !== 'string')
                return Reflect.get(algebra, prop);
            if (prop === 'fold')
                return foldInstance;
            if (prop === 'is')
                return assertions;
            if (prop === 'wide')
                return constructors;
            return constructors[prop];
        },
    });
};
/**
 * Build a record of prisms to match the elements of a tagged type.
 *
 * @example
 * type NumStr = tagged.Tagged<'num', number> | tagged.Tagged<'str', string>;
 * const numStr = tagged.build<NumStr>();
 * const prisms = tagged.prisms<NumStr>();
 * prisms.num.getOption(numStr.num(1)) // some(1)
 * prisms.str.getOption(numStr.num(1)) // none
 */
export const prisms = () => prism.fromTag('_tag');
/**
 * Build an Eq instance for a tagged type.
 * Instances have to be provided only for members with values.
 */
export const getEq = (eqs) => ({
    equals: (a, b) => a === b ||
        (a._tag === b._tag && (!(a._tag in eqs) || eqs[a._tag].equals(a.value, b.value))),
});
export const value = (t) => t.value;
//# sourceMappingURL=tagged.js.map